import { useEffect, useRef, useState } from "react";

const TAGS = [
    { label: "React.js", color: [47, 143, 111] },
    { label: "Next.js", color: [215, 199, 161] },
    { label: "TypeScript", color: [47, 143, 111] },
    { label: "JavaScript", color: [215, 199, 161] },
    { label: "Redux", color: [47, 143, 111] },
    { label: "Framer Motion", color: [215, 199, 161] },
    { label: "GSAP", color: [47, 143, 111] },
    { label: "GoLang", color: [20, 90, 67] },
    { label: "Figma", color: [215, 199, 161] },
    { label: "Node.js", color: [47, 143, 111] },
    { label: "Tailwind CSS", color: [20, 90, 67] },
    { label: "Bootstrap", color: [47, 143, 111] },
    { label: "Git", color: [215, 199, 161] },
    { label: "MySQL", color: [47, 143, 111] },
    { label: "REST API", color: [215, 199, 161] },
    { label: "Corel Draw", color: [20, 90, 67] },
    { label: "Photoshop", color: [47, 143, 111] },
    { label: "Jira", color: [215, 199, 161] },
    { label: "Notion", color: [47, 143, 111] },
    { label: "React Native", color: [20, 90, 67] },
    { label: "CSS3", color: [47, 143, 111] },
    { label: "HTML5", color: [215, 199, 161] },
    { label: "Vite", color: [47, 143, 111] },
];

export default function SkillOrb() {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [activeTag, setActiveTag] = useState(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;
        const ctx = canvas.getContext("2d");

        const numTags = TAGS.length;
        const radius = 145;
        const depth = 380;
        let tagsData = TAGS.map((tag, i) => {
            const phi = Math.acos(-1 + (2 * i) / numTags);
            const theta = Math.sqrt(numTags * Math.PI) * phi;
            return {
                ...tag,
                x: radius * Math.sin(phi) * Math.cos(theta),
                y: radius * Math.sin(phi) * Math.sin(theta),
                z: radius * Math.cos(phi),
                measuredWidth: 0,
                hoverScale: 1,
            };
        });

        let width, height;
        let speedX = 0.003;
        let speedY = 0.004;
        let targetSpeedX = 0.003;
        let targetSpeedY = 0.004;
        let lastMouseX = 0, lastMouseY = 0;
        let isDragging = false;
        let hoveredIndex = -1;

        const resize = () => {
            const w = container.clientWidth;
            width = canvas.width = w;
            height = canvas.height = Math.min(w, 420);
        };
        resize();
        window.addEventListener("resize", resize);

        const rotateX = (angle) => {
            const cos = Math.cos(angle), sin = Math.sin(angle);
            tagsData.forEach(t => {
                const y1 = t.y * cos - t.z * sin;
                const z1 = t.y * sin + t.z * cos;
                t.y = y1; t.z = z1;
            });
        };
        const rotateY = (angle) => {
            const cos = Math.cos(angle), sin = Math.sin(angle);
            tagsData.forEach(t => {
                const x1 = t.x * cos - t.z * sin;
                const z1 = t.x * sin + t.z * cos;
                t.x = x1; t.z = z1;
            });
        };

        const getMousePos = (e) => {
            const rect = canvas.getBoundingClientRect();
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            return { x: clientX - rect.left, y: clientY - rect.top };
        };

        const onPointerDown = (e) => {
            isDragging = true;
            const pos = getMousePos(e);
            lastMouseX = pos.x; lastMouseY = pos.y;
            if (e.setPointerCapture) e.target.setPointerCapture(e.pointerId);
        };
        const onPointerMove = (e) => {
            const pos = getMousePos(e);
            if (!isDragging) {
                const mx = pos.x - width / 2;
                const my = pos.y - height / 2;
                targetSpeedY = (mx / (width / 2)) * 0.012;
                targetSpeedX = (-my / (height / 2)) * 0.012;

                const centerX = width / 2, centerY = height / 2;
                let found = -1;
                const sorted = [...tagsData].sort((a, b) => b.z - a.z);
                for (let i = 0; i < sorted.length; i++) {
                    const tag = sorted[i];
                    const scale = depth / (depth + tag.z);
                    const x2d = centerX + tag.x * scale;
                    const y2d = centerY + tag.y * scale;
                    const fontSize = Math.max(10, Math.floor(11 + scale * 4));
                    const halfW = (tag.measuredWidth * scale + 24 * scale) / 2;
                    const halfH = (fontSize + 12 * scale) / 2;
                    if (pos.x >= x2d - halfW && pos.x <= x2d + halfW && pos.y >= y2d - halfH && pos.y <= y2d + halfH) {
                        found = tagsData.indexOf(tag);
                        break;
                    }
                }
                hoveredIndex = found;
                canvas.style.cursor = found !== -1 ? "pointer" : "grab";
                if (found !== -1) setActiveTag(tagsData[found].label);
                else setActiveTag(null);
            } else {
                const dx = pos.x - lastMouseX;
                const dy = pos.y - lastMouseY;
                lastMouseX = pos.x; lastMouseY = pos.y;
                speedY = dx * 0.003;
                speedX = -dy * 0.003;
                targetSpeedX = speedX;
                targetSpeedY = speedY;
            }
        };
        const onPointerUp = () => {
            isDragging = false;
            canvas.style.cursor = "grab";
        };
        const onPointerLeave = () => {
            isDragging = false;
            hoveredIndex = -1;
            canvas.style.cursor = "grab";
            targetSpeedX = 0.003;
            targetSpeedY = 0.004;
            setActiveTag(null);
        };

        canvas.addEventListener("pointerdown", onPointerDown);
        canvas.addEventListener("pointermove", onPointerMove);
        canvas.addEventListener("pointerup", onPointerUp);
        canvas.addEventListener("pointerleave", onPointerLeave);

        let animId;
        const render = () => {
            ctx.clearRect(0, 0, width, height);

            speedX += (targetSpeedX - speedX) * 0.07;
            speedY += (targetSpeedY - speedY) * 0.07;
            rotateX(speedX);
            rotateY(speedY);

            const sorted = [...tagsData].sort((a, b) => b.z - a.z);
            const centerX = width / 2, centerY = height / 2;

            ctx.save();
            ctx.beginPath();
            ctx.ellipse(centerX, centerY, radius * 1.02, radius * 0.28, 0, 0, Math.PI * 2);
            ctx.strokeStyle = "rgba(47, 143, 111, 0.06)";
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.restore();

            sorted.forEach((tag) => {
                const idx = tagsData.indexOf(tag);
                const isHovered = idx === hoveredIndex;
                const scale = depth / (depth + tag.z);
                const x2d = centerX + tag.x * scale;
                const y2d = centerY + tag.y * scale;

                const normalizedZ = (tag.z + radius) / (2 * radius);
                const alpha = Math.pow(normalizedZ, 1.5) * 0.75 + 0.12;

                const targetHoverScale = isHovered ? 1.18 : 1;
                tag.hoverScale = tag.hoverScale + (targetHoverScale - tag.hoverScale) * 0.15;
                const hs = tag.hoverScale;

                ctx.save();
                ctx.translate(x2d, y2d);
                ctx.scale(hs, hs);

                const fontSize = Math.max(10, Math.floor(10.5 + scale * 4.5));
                ctx.font = `600 ${fontSize}px "SF Pro Display", system-ui, sans-serif`;

                if (tag.measuredWidth === 0) {
                    tag.measuredWidth = ctx.measureText(tag.label).width;
                }

                const paddingX = 13 * scale;
                const paddingY = 6 * scale;
                const cardW = tag.measuredWidth * scale + paddingX * 2;
                const cardH = fontSize + paddingY * 2;
                const cornerR = 20 * scale;

                const [r, g, b] = tag.color;

                if (isHovered || normalizedZ > 0.78) {
                    const glowA = isHovered ? 0.35 : (normalizedZ - 0.78) / 0.22 * 0.12;
                    ctx.shadowColor = `rgba(${r}, ${g}, ${b}, ${glowA})`;
                    ctx.shadowBlur = isHovered ? 18 : 10;
                }

                ctx.beginPath();
                ctx.roundRect(-cardW / 2, -cardH / 2, cardW, cardH, cornerR);

                if (isHovered) {
                    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.18)`;
                } else {
                    ctx.fillStyle = `rgba(11, 61, 46, ${0.55 * alpha})`;
                }
                ctx.fill();

                ctx.strokeStyle = isHovered
                    ? `rgba(${r}, ${g}, ${b}, 0.9)`
                    : `rgba(${r}, ${g}, ${b}, ${0.30 * alpha})`;
                ctx.lineWidth = Math.max(0.5, (isHovered ? 1.2 : 0.75) * scale);
                ctx.shadowBlur = 0;
                ctx.stroke();

                ctx.fillStyle = isHovered
                    ? `rgba(${r}, ${g}, ${b}, 1)`
                    : `rgba(215, 199, 161, ${alpha * 0.9 + 0.08})`;
                ctx.textBaseline = "middle";
                ctx.textAlign = "center";
                ctx.shadowBlur = 0;
                ctx.fillText(tag.label, 0, 0);

                ctx.restore();
            });

            animId = requestAnimationFrame(render);
        };
        render();

        return () => {
            cancelAnimationFrame(animId);
            window.removeEventListener("resize", resize);
        };
    }, []);

    return (
        <div
            style={{
                fontFamily: "'SF Pro Display', system-ui, sans-serif",
                background: "#0F0F0F",
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "48px 24px",
            }}
        >
            <div style={{ textAlign: "center", marginBottom: "40px" }}>
                <div style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    background: "rgba(47, 143, 111, 0.10)",
                    border: "1px solid rgba(47, 143, 111, 0.25)",
                    borderRadius: "100px",
                    padding: "6px 16px",
                    marginBottom: "20px",
                }}>
                    <span style={{
                        width: "7px", height: "7px", borderRadius: "50%",
                        background: "#2F8F6F",
                        boxShadow: "0 0 8px rgba(47, 143, 111, 0.8)",
                        display: "inline-block",
                    }} />
                    <span style={{
                        fontSize: "11px", fontWeight: 600,
                        letterSpacing: "0.12em", textTransform: "uppercase",
                        color: "#2F8F6F",
                    }}>
                        Tech Stack
                    </span>
                </div>

                <h1 style={{
                    margin: "0 0 12px",
                    fontSize: "clamp(28px, 5vw, 42px)",
                    fontWeight: 700,
                    letterSpacing: "-0.03em",
                    color: "#f0f0f0",
                    lineHeight: 1.1,
                }}>
                    Skills &amp; Technologies
                </h1>

                <p style={{
                    margin: 0,
                    fontSize: "15px",
                    color: "rgba(215, 199, 161, 0.45)",
                    maxWidth: "340px",
                }}>
                    Drag or hover to explore the interactive skill sphere
                </p>
            </div>

            <div
                ref={containerRef}
                style={{
                    position: "relative",
                    width: "100%",
                    maxWidth: "460px",
                }}
            >
                <div style={{
                    position: "absolute",
                    inset: "20px",
                    borderRadius: "50%",
                    background: "radial-gradient(ellipse, rgba(11, 61, 46, 0.4) 0%, transparent 70%)",
                    pointerEvents: "none",
                    filter: "blur(30px)",
                }} />

                <canvas
                    ref={canvasRef}
                    style={{
                        display: "block",
                        width: "100%",
                        cursor: "grab",
                        touchAction: "none",
                        borderRadius: "24px",
                    }}
                />

                <div style={{
                    position: "absolute",
                    bottom: "16px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    pointerEvents: "none",
                    transition: "opacity 0.25s ease",
                    opacity: activeTag ? 1 : 0,
                }}>
                    <div style={{
                        background: "rgba(47, 143, 111, 0.15)",
                        border: "1px solid rgba(47, 143, 111, 0.4)",
                        borderRadius: "100px",
                        padding: "5px 14px",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#D7C7A1",
                        whiteSpace: "nowrap",
                        backdropFilter: "blur(8px)",
                    }}>
                        {activeTag}
                    </div>
                </div>
            </div>

            <div style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                justifyContent: "center",
                marginTop: "36px",
                maxWidth: "480px",
            }}>
                {TAGS.map(tag => (
                    <div
                        key={tag.label}
                        style={{
                            padding: "4px 12px",
                            borderRadius: "100px",
                            background: `rgba(${tag.color[0]},${tag.color[1]},${tag.color[2]},0.08)`,
                            border: `1px solid rgba(${tag.color[0]},${tag.color[1]},${tag.color[2]},0.22)`,
                            fontSize: "11px",
                            fontWeight: 600,
                            color: `rgb(${tag.color[0]},${tag.color[1]},${tag.color[2]})`,
                            letterSpacing: "0.02em",
                            transition: "background 0.2s",
                            cursor: "default",
                        }}
                    >
                        {tag.label}
                    </div>
                ))}
            </div>
        </div>
    );
}